// src/hooks/useAudio.ts
// React hook over the audioService singleton.
// Subscribes to state changes via audioService.subscribe() and exposes
// play/pause/seek controls. Mount anywhere; shared singleton means all
// instances stay in sync.

import { useCallback, useEffect, useState } from 'react';
import { audioService, type AudioServiceState } from '../services/audioService';

// ── Return type ───────────────────────────────────────────────────────────────

export interface UseAudioReturn {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isSeeking: boolean;
  currentUnitId: string | null;
  /** Toggle play/pause for a unit. Pass the audioUrl from unitMediaCache. */
  playPause: (unitId: string, audioUrl: string) => Promise<void>;
  seekBackward: () => void;
  seekForward: () => void;
  /** Seek to position by percentage 0–100. */
  seekTo: (percent: number) => void;
  stopAll: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAudio(): UseAudioReturn {
  const [state, setState] = useState<AudioServiceState>(() => ({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    isSeeking: false,
    currentUnitId: null,
  }));

  useEffect(() => {
    // Subscribe returns an unsubscribe function. audioService also calls cb
    // immediately with the current snapshot so state is correct on mount.
    const unsubscribe = audioService.subscribe((nextState) => {
      setState(nextState);
    });
    return unsubscribe;
  }, []);

  const playPause = useCallback(
    (unitId: string, audioUrl: string) => audioService.playPause(unitId, audioUrl),
    [],
  );

  const seekBackward = useCallback(() => audioService.seekBackward(), []);
  const seekForward  = useCallback(() => audioService.seekForward(), []);
  const seekTo       = useCallback((pct: number) => audioService.seekTo(pct), []);
  const stopAll      = useCallback(() => audioService.stopAll(), []);

  return {
    ...state,
    playPause,
    seekBackward,
    seekForward,
    seekTo,
    stopAll,
  };
}